import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Paper, Table, TableHead, TableRow, TableCell, TableBody, Chip, Switch, Button, TextField, Select, MenuItem, Stack, Typography, Alert } from '@mui/material';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import PageHeader from '../../components/common/PageHeader';
import StatCard from '../../components/common/StatCard';
import { loadScheduledTasks, createScheduledTaskRecord, toggleScheduledTaskActive } from './schedulerSlice';
import { formatRelativeTime } from '../../utils/severity';
import { tokens } from '../../theme/theme';
import useCurrentPermissions from '../../hooks/useCurrentPermissions';

const RUN_STATUS_COLOR = { never_run: tokens.text.muted, success: tokens.severity.low, failed: tokens.severity.critical };
const TASK_TYPES = ['NETWORK_DISCOVERY_SCAN', 'REPORT_GENERATION', 'DATA_RETENTION_CLEANUP'];

export default function SchedulerPage() {
  const dispatch = useDispatch();
  const { items, status, error } = useSelector((s) => s.scheduler);
  const { can, loaded } = useCurrentPermissions();
  const canWrite = can('scheduler:write');
  const [name, setName] = useState('');
  const [taskType, setTaskType] = useState('NETWORK_DISCOVERY_SCAN');
  const [intervalMinutes, setIntervalMinutes] = useState(1440);

  useEffect(() => { dispatch(loadScheduledTasks()); }, [dispatch]);

  const active = items.filter((t) => t.active).length;
  const failed = items.filter((t) => t.lastRunStatus === 'failed').length;

  const handleCreate = () => {
    if (!name.trim()) return;
    dispatch(createScheduledTaskRecord({ name: name.trim(), taskType, intervalMinutes: Number(intervalMinutes) })).unwrap().then(() => setName('')).catch(() => {});
  };

  return (
    <Box>
      <PageHeader title="Scheduler" subtitle="Recurring platform tasks — network discovery sweeps, report generation, data retention cleanup" />
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={4}><StatCard label="Total Tasks" value={items.length} icon={ScheduleRoundedIcon} /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Active" value={active} accent={tokens.severity.low} /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Last Run Failed" value={failed} accent={tokens.severity.critical} /></Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loaded && !canWrite && <Alert severity="info" sx={{ mb: 2 }}>Your role has read-only scheduler access. Ask an administrator for scheduler:write permission to create, enable, or disable scheduled tasks.</Alert>}
      <Paper sx={{ p: 2, mb: 2.5 }}>
        <Stack direction="row" spacing={1.5}>
          <TextField disabled={!canWrite} size="small" fullWidth placeholder="Task name" value={name} onChange={(e) => setName(e.target.value)} />
          <Select disabled={!canWrite} size="small" value={taskType} onChange={(e) => setTaskType(e.target.value)} sx={{ minWidth: 220 }}>
            {TASK_TYPES.map((t) => <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>)}
          </Select>
          <TextField disabled={!canWrite} size="small" type="number" label="Interval (minutes)" value={intervalMinutes} inputProps={{ min: 1 }} onChange={(e) => setIntervalMinutes(Math.max(1, Number(e.target.value) || 1))} sx={{ width: 170 }} />
          <Button variant="contained" disabled={!canWrite || status === 'loading'} onClick={handleCreate}>Schedule Task</Button>
        </Stack>
      </Paper>

      <Paper sx={{ overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Name', 'Type', 'Interval', 'Last Run', 'Next Run', 'Active'].map((h) => (
                <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.66rem', textTransform: 'uppercase' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((t) => (
              <TableRow key={t.id} hover>
                <TableCell sx={{ fontSize: '0.8rem' }}>{t.name}</TableCell>
                <TableCell sx={{ fontSize: '0.72rem', color: tokens.text.muted }}>{t.taskType.replace(/_/g, ' ')}</TableCell>
                <TableCell sx={{ fontSize: '0.76rem' }}>{t.intervalMinutes >= 1440 ? `${Math.round(t.intervalMinutes / 1440)}d` : `${t.intervalMinutes}m`}</TableCell>
                <TableCell>
                  {t.lastRunAt ? (
                    <Chip label={t.lastRunStatus.replace('_', ' ')} size="small" sx={{ color: RUN_STATUS_COLOR[t.lastRunStatus], border: `1px solid ${RUN_STATUS_COLOR[t.lastRunStatus]}`, backgroundColor: 'transparent' }} />
                  ) : <Typography sx={{ fontSize: '0.72rem', color: tokens.text.muted }}>never run</Typography>}
                </TableCell>
                <TableCell sx={{ fontSize: '0.7rem', color: tokens.text.muted, whiteSpace: 'nowrap' }}>{formatRelativeTime(t.nextRunAt)}</TableCell>
                <TableCell>
                  <Switch disabled={!canWrite} size="small" checked={t.active} onChange={(e) => dispatch(toggleScheduledTaskActive({ id: t.id, active: e.target.checked }))} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
